import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { PRODUCTION_STATES, calculateBreakEven } from '../../config/productionTemplates';

/**
 * TierProductionConfig Component
 *
 * Configure production constraints and methods for each tier.
 * Based on Universal Tier Production Template system.
 *
 * @param {array} tiers - Available pricing tiers from calculator
 * @param {object} tierProductionConfigs - Current production configs by tier number
 * @param {function} onUpdateTierProduction - Callback when production config changes
 * @param {object} productionTemplate - Auto-populated template based on category
 */
const TierProductionConfig = ({
  tiers = [],
  tierProductionConfigs = {},
  onUpdateTierProduction,
  productionTemplate
}) => {
  const [expandedTier, setExpandedTier] = useState(1);

  // Initialize production configs from template if not already set
  useEffect(() => {
    if (productionTemplate && Object.keys(tierProductionConfigs).length === 0) {
      const initialConfigs = {};

      Object.entries(productionTemplate.tiers).forEach(([tierNum, tierTemplate]) => {
        initialConfigs[tierNum] = {
          ...tierTemplate,
          tierNumber: parseInt(tierNum)
        };
      });

      onUpdateTierProduction(initialConfigs);
    }
  }, [productionTemplate]);

  const handleFieldChange = (tierNumber, field, value) => {
    const updatedConfig = {
      ...tierProductionConfigs[tierNumber],
      [field]: value
    };

    onUpdateTierProduction({
      ...tierProductionConfigs,
      [tierNumber]: updatedConfig
    });
  };

  const handleThresholdChange = (tierNumber, minOrMax, value) => {
    const currentThreshold = tierProductionConfigs[tierNumber]?.threshold || {};
    const updatedThreshold = {
      ...currentThreshold,
      [minOrMax]: value === '' ? null : parseInt(value)
    };

    handleFieldChange(tierNumber, 'threshold', updatedThreshold);
  };

  const getTierPrice = (tierNumber) => {
    const tier = tiers.find(t => t.tierNumber === tierNumber);
    return tier?.price || null;
  };

  const getBreakEvenAnalysis = (tierNumber) => {
    const config = tierProductionConfigs[tierNumber];
    if (!config) return null;

    const price = getTierPrice(tierNumber);
    const cost = parseFloat(config.costPerUnit);
    const setupCost = parseFloat(config.setupCost);

    if (!price || !cost) return null;

    const breakEven = calculateBreakEven(setupCost, price, cost);
    const threshold = config.threshold?.min || 0;

    return {
      breakEven,
      isProfitable: threshold >= breakEven,
      margin: price - cost,
      marginPercent: ((price - cost) / price * 100).toFixed(1)
    };
  };

  const getProductionStateColor = (state) => {
    switch (state) {
      case PRODUCTION_STATES.ON_DEMAND:
        return 'success';
      case PRODUCTION_STATES.MICRO_BATCH:
        return 'warning';
      case PRODUCTION_STATES.FULL_BATCH:
        return 'primary';
      default:
        return 'default';
    }
  };

  const getProductionStateLabel = (state) => {
    switch (state) {
      case PRODUCTION_STATES.ON_DEMAND:
        return 'On-Demand';
      case PRODUCTION_STATES.MICRO_BATCH:
        return 'Micro-Batch';
      case PRODUCTION_STATES.FULL_BATCH:
        return 'Full Batch';
      default:
        return state;
    }
  };

  if (!tiers || tiers.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Run the pricing calculator first to generate tiers.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Production & Constraints by Tier
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure production methods, thresholds, and costs for each tier.
        {productionTemplate && ` Auto-populated for: ${productionTemplate.name}`}
      </Typography>

      {[1, 2, 3].map((tierNumber) => {
        const config = tierProductionConfigs[tierNumber] || {};
        const tier = tiers.find(t => t.tierNumber === tierNumber);
        const analysis = getBreakEvenAnalysis(tierNumber);

        if (!tier) return null;

        return (
          <Accordion
            key={tierNumber}
            expanded={expandedTier === tierNumber}
            onChange={(e, isExpanded) => setExpandedTier(isExpanded ? tierNumber : null)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography variant="subtitle1" fontWeight="bold">
                  Tier {tierNumber}: {config.name || `Tier ${tierNumber}`}
                </Typography>

                {config.state && (
                  <Chip
                    label={getProductionStateLabel(config.state)}
                    color={getProductionStateColor(config.state)}
                    size="small"
                  />
                )}

                <Box sx={{ ml: 'auto', mr: 2 }}>
                  {analysis && (
                    analysis.isProfitable ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Profitable"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        icon={<WarningIcon />}
                        label="Check threshold"
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    )
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Production State */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Production State</InputLabel>
                    <Select
                      value={config.state || ''}
                      onChange={(e) => handleFieldChange(tierNumber, 'state', e.target.value)}
                      label="Production State"
                    >
                      <MenuItem value={PRODUCTION_STATES.ON_DEMAND}>On-Demand / Unit-Based</MenuItem>
                      <MenuItem value={PRODUCTION_STATES.MICRO_BATCH}>Micro-Batch</MenuItem>
                      <MenuItem value={PRODUCTION_STATES.FULL_BATCH}>Full Batch</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Production Method */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Production Method"
                    value={config.method || ''}
                    onChange={(e) => handleFieldChange(tierNumber, 'method', e.target.value)}
                    placeholder="e.g., POD DTF, Screen Print, Cut & Sew"
                    helperText="Specific manufacturing technique for this tier"
                  />
                </Grid>

                {/* Unit Threshold */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Unit Threshold
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <TextField
                        type="number"
                        label="Min"
                        value={config.threshold?.min || ''}
                        onChange={(e) => handleThresholdChange(tierNumber, 'min', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Typography>to</Typography>
                      <TextField
                        type="number"
                        label="Max"
                        value={config.threshold?.max || ''}
                        onChange={(e) => handleThresholdChange(tierNumber, 'max', e.target.value)}
                        placeholder="∞"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">units</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Lead Time */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Lead Time (days)"
                    value={config.leadTimeDays || ''}
                    onChange={(e) => handleFieldChange(tierNumber, 'leadTimeDays', e.target.value)}
                    helperText="Production time from order close to ship"
                  />
                </Grid>

                {/* Cost Per Unit */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cost per Unit ($)"
                    value={config.costPerUnit || ''}
                    onChange={(e) => handleFieldChange(tierNumber, 'costPerUnit', e.target.value)}
                    helperText="Production cost per unit"
                  />
                </Grid>

                {/* Setup Cost */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Setup Cost ($)"
                    value={config.setupCost || ''}
                    onChange={(e) => handleFieldChange(tierNumber, 'setupCost', e.target.value)}
                    helperText="One-time setup/tooling cost"
                  />
                </Grid>

                {/* Supplier */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Supplier (optional)"
                    value={config.supplier || ''}
                    onChange={(e) => handleFieldChange(tierNumber, 'supplier', e.target.value)}
                    placeholder="e.g., Printful, Local Partner"
                    helperText="Production partner"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Break-even Analysis */}
                {analysis && (
                  <Grid item xs={12}>
                    <Alert
                      severity={analysis.isProfitable ? 'success' : 'warning'}
                      icon={analysis.isProfitable ? <CheckCircleIcon /> : <WarningIcon />}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {analysis.isProfitable ? 'Tier is Profitable' : 'Threshold Too Low'}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          • Break-even: <strong>{analysis.breakEven === 0 ? 'N/A (no setup cost)' : `${analysis.breakEven} units`}</strong>
                        </Typography>
                        <Typography variant="body2">
                          • Margin: <strong>${analysis.margin.toFixed(2)} ({analysis.marginPercent}%)</strong>
                        </Typography>
                        <Typography variant="body2">
                          • Price: <strong>${getTierPrice(tierNumber)}</strong> | Cost: <strong>${config.costPerUnit}</strong>
                        </Typography>
                        {!analysis.isProfitable && analysis.breakEven > 0 && (
                          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                            ⚠️ Increase minimum threshold to at least {analysis.breakEven} units to cover setup costs
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  </Grid>
                )}

                {/* Production Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Production Notes"
                    value={config.notes || ''}
                    onChange={(e) => handleFieldChange(tierNumber, 'notes', e.target.value)}
                    placeholder="e.g., Artwork can still change, QC workflow, packaging requirements"
                    helperText="Internal notes about this tier's production workflow"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Template Info */}
      {productionTemplate && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Template Applied:</strong> {productionTemplate.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Based on product category. You can customize all fields above.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default TierProductionConfig;
